import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AvailabilityIndicator } from './AvailabilityIndicator';
import { TrustSeals } from './TrustSeals';
import { FormFieldDynamic } from './FormFieldDynamic';
import { ArrowRight } from 'lucide-react';

interface InscricaoPreviewProps {
  config: any;
  className?: string;
}

export function InscricaoPreview({ config, className = '' }: InscricaoPreviewProps) {
  useLanguage();
  const [debouncedConfig, setDebouncedConfig] = useState(config);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConfig(config);
    }, 250);

    return () => clearTimeout(timer);
  }, [config]);

  return (
    <div className={`${className}`}>
      <div 
        className="w-full origin-top"
        style={{
          transform: 'scale(0.85)',
          transformOrigin: 'top center'
        }}
      >
        <Card 
          className="border shadow-[var(--wizard-shadow-preview)] rounded-[var(--wizard-card-radius)] overflow-hidden"
          style={{
            backgroundColor: debouncedConfig.background_color,
            backgroundImage: debouncedConfig.background_image_url ? `url(${debouncedConfig.background_image_url})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-label={translateInterfaceText("Prévia da página de inscrição")}
        >
          <CardContent className="p-6 space-y-5">
            {/* Logo */}
            {debouncedConfig.logo_url && (
              <div className={`flex ${debouncedConfig.logo_alignment === 'center' ? 'justify-center' : debouncedConfig.logo_alignment === 'right' ? 'justify-end' : 'justify-start'}`}>
                <img src={debouncedConfig.logo_url} alt={translateInterfaceText("Logo")} className="h-8 object-contain" />
              </div>
            )}

            {/* Availability Indicator */}
            <AvailabilityIndicator
              type={debouncedConfig.availability_type}
              thickness={debouncedConfig.availability_thickness}
              colorBase={debouncedConfig.availability_color_base}
              colorProgress={debouncedConfig.availability_color_progress}
              progress={debouncedConfig.availability_initial}
              label={debouncedConfig.availability_label}
            />

            {/* Title & Subtitle */}
            <div className="space-y-2">
              <h2 className="text-2xl font-bold leading-tight">
                {debouncedConfig.page_title || translateInterfaceText("Título do Webinar")}
              </h2>
              <p className="text-base text-muted-foreground">
                {debouncedConfig.page_subtitle || translateInterfaceText("Subtítulo do webinar")}
              </p>
            </div>

            {/* Form Fields */}
            <div className="space-y-3">
              {debouncedConfig.form_fields
                .filter((field: any) => field.enabled)
                .map((field: any) => (
                  <FormFieldDynamic
                    key={field.id}
                    field={field}
                    value=""
                    onChange={() => {}}
                    error=""
                  />
                ))}
            </div>

            {/* LGPD */}
            {debouncedConfig.lgpd_enabled && (
              <div className="flex items-start gap-2">
                <Checkbox id="consent-preview" disabled />
                <label htmlFor="consent-preview" className="text-xs text-muted-foreground leading-tight">
                  {debouncedConfig.lgpd_text}
                  {debouncedConfig.lgpd_required && <span className="text-destructive ml-1">*</span>}
                </label>
              </div>
            )}

            {/* CTA Button */}
            <Button
              className="w-full h-11 text-base font-semibold group transition-all hover:translate-y-[-2px] hover:shadow-lg"
              variant={debouncedConfig.cta_style === 'outline' ? 'outline' : 'default'}
              disabled
            >
              <span>{debouncedConfig.cta_label || translateInterfaceText("Entrar na Sala")}</span>
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>

            {/* Trust Seals */}
            <TrustSeals
              seals={debouncedConfig.trust_seals}
              enabled={debouncedConfig.trust_seals_enabled}
            />

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <span>{translateInterfaceText("🔒 Site seguro")}</span>
              <span>•</span>
              <span>{translateInterfaceText("Privacidade protegida")}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
